import { useCallback, useEffect, useState } from "react";
import { api, fmtEUR } from "../lib/api";
import { primaryImg } from "../lib/img";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Trash2, Plus, Edit2 } from "lucide-react";
import { toast } from "sonner";
import ArticleEditor from "../components/ArticleEditor";
import StatsPanel from "../components/StatsPanel";
import ProductImageUploader from "../components/ProductImageUploader";
import RichTextEditor from "../components/RichTextEditor";
import ReviewsModeration from "../components/ReviewsModeration";
import HomePageCMS from "../components/HomePageCMS";
import SEOFields from "../components/SEOFields";

const empty = { name: "", description: "", category: "Nutrition", price: 0, promo_price: null, image_url: "", images: [], is_promo: false, is_limited: false, is_featured: false, is_new: false, stock: 100, seo: {} };

function ProductForm({ initial, onSubmit, onCancel }) {
  const [f, setF] = useState(() => {
    const init = initial || empty;
    // Normalize: ensure images is an array
    const imgs = Array.isArray(init.images) && init.images.length > 0
      ? init.images
      : (init.image_url ? [init.image_url] : []);
    return { ...init, images: imgs };
  });
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    if (!f.images || f.images.length === 0) {
      toast.error("Ajoutez au moins une image");
      return;
    }
    // RichTextEditor stores HTML; strip tags to verify there's actual content.
    const plain = (f.description || "").replace(/<[^>]*>/g, "").trim();
    if (!plain) {
      toast.error("La description est obligatoire");
      return;
    }
    onSubmit({ ...f, image_url: f.images[0], images: f.images });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div><Label>Images du produit</Label><ProductImageUploader images={f.images} onChange={(imgs) => set("images", imgs)} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Nom</Label><Input value={f.name} onChange={e => set("name", e.target.value)} required data-testid="product-form-name" /></div>
        <div className="col-span-2"><Label>Description</Label><RichTextEditor value={f.description} onChange={(v) => set("description", v)} testId="product-form-description" /></div>
        <div><Label>Catégorie</Label><Input value={f.category} onChange={e => set("category", e.target.value)} data-testid="product-form-category" /></div>
        <div><Label>Stock</Label><Input type="number" value={f.stock} onChange={e => set("stock", parseInt(e.target.value || 0))} data-testid="product-form-stock" /></div>
        <div><Label>Prix (EUR)</Label><Input type="number" step="0.01" value={f.price} onChange={e => set("price", parseFloat(e.target.value || 0))} required data-testid="product-form-price" /></div>
        <div><Label>Prix promo (optionnel)</Label><Input type="number" step="0.01" value={f.promo_price ?? ""} onChange={e => set("promo_price", e.target.value ? parseFloat(e.target.value) : null)} data-testid="product-form-promo-price" /></div>
        <label className="flex items-center justify-between border border-[#E5EAE5] rounded-xl px-4 py-3"><span className="text-sm font-medium">En promotion</span><Switch checked={f.is_promo} onCheckedChange={(v) => set("is_promo", v)} data-testid="product-form-is-promo" /></label>
        <label className="flex items-center justify-between border border-[#E5EAE5] rounded-xl px-4 py-3"><span className="text-sm font-medium">Édition limitée</span><Switch checked={f.is_limited} onCheckedChange={(v) => set("is_limited", v)} data-testid="product-form-is-limited" /></label>
        <label className="flex items-center justify-between border border-[#E5EAE5] rounded-xl px-4 py-3"><span className="text-sm font-medium">⭐ Coup de cœur</span><Switch checked={f.is_featured} onCheckedChange={(v) => set("is_featured", v)} data-testid="product-form-is-featured" /></label>
        <label className="flex items-center justify-between border border-[#E5EAE5] rounded-xl px-4 py-3"><span className="text-sm font-medium">🆕 Nouveauté</span><Switch checked={f.is_new} onCheckedChange={(v) => set("is_new", v)} data-testid="product-form-is-new" /></label>
      </div>
      <details className="border border-[#E5EAE5] rounded-xl">
        <summary className="cursor-pointer px-4 py-3 font-semibold text-sm" data-testid="product-form-seo-toggle">SEO du produit (avancé)</summary>
        <div className="p-4 border-t border-[#E5EAE5]">
          <SEOFields value={f.seo || {}} onChange={(seo) => set("seo", seo)} testIdPrefix="product-form-seo" />
        </div>
      </details>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
        <Button type="submit" className="bg-[#7AC143] hover:bg-[#67BC2A]" data-testid="product-form-submit">Enregistrer</Button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    const [p, o, u] = await Promise.all([
      api.get("/products"),
      api.get("/admin/orders"),
      api.get("/admin/users"),
    ]);
    setProducts(p.data); setOrders(o.data); setUsers(u.data);
  }, []);
  useEffect(() => { refresh().catch(() => {}); }, [refresh]);

  const save = async (f) => {
    try {
      if (editing) await api.put(`/products/${editing.id}`, f);
      else await api.post("/products", f);
      toast.success("Enregistré");
      setOpen(false); setEditing(null);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Supprimé");
    await refresh();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12" data-testid="admin-dashboard">
      <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#7AC143] mb-3">Administration</div>
      <h1 className="text-4xl font-black tracking-tighter">Tableau de bord</h1>

      <Tabs defaultValue="stats" className="mt-10">
        <TabsList>
          <TabsTrigger value="stats" data-testid="admin-tab-stats">Statistiques</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">Produits</TabsTrigger>
          <TabsTrigger value="articles" data-testid="admin-tab-articles">Articles</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Avis</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Commandes</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Clients</TabsTrigger>
          <TabsTrigger value="home" data-testid="admin-tab-home">Page d'accueil</TabsTrigger>
        </TabsList>

        <TabsContent value="stats" className="mt-6">
          <StatsPanel />
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#7AC143] hover:bg-[#67BC2A]" data-testid="admin-add-product-btn"><Plus className="w-4 h-4 mr-2" /> Nouveau produit</Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouveau"} produit</DialogTitle></DialogHeader>
                <ProductForm initial={editing} onSubmit={save} onCancel={() => setOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-[#E5EAE5] rounded-2xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Produit</TableHead><TableHead>Prix</TableHead><TableHead>Statut</TableHead><TableHead>Stock</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {products.map(p => (
                  <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`}>
                    <TableCell><div className="flex items-center gap-3"><img src={primaryImg(p)} className="w-12 h-12 rounded-lg object-cover" alt="" /><span className="font-semibold">{p.name}</span></div></TableCell>
                    <TableCell>{fmtEUR(p.is_promo && p.promo_price ? p.promo_price : p.price)}</TableCell>
                    <TableCell><div className="flex gap-1">{p.is_promo && <Badge className="bg-[#7AC143]">Promo</Badge>}{p.is_limited && <Badge className="bg-[#FACC15] text-[#1A2E1A]">Limitée</Badge>}</div></TableCell>
                    <TableCell>{p.stock}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`admin-edit-${p.id}`}><Edit2 className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(p.id)} data-testid={`admin-delete-${p.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="articles" className="mt-6">
          <ArticleEditor />
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          <ReviewsModeration />
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="bg-white border border-[#E5EAE5] rounded-2xl overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Client</TableHead><TableHead>Articles</TableHead><TableHead>Montant</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
              <TableBody>
                {orders.map(o => (
                  <TableRow key={o.session_id} data-testid={`admin-order-${o.session_id}`}>
                    <TableCell className="text-sm text-[#4B634B]">{new Date(o.created_at).toLocaleString("fr-FR")}</TableCell>
                    <TableCell>{o.user_email}</TableCell>
                    <TableCell className="text-sm">{(o.line_summary || []).join(", ")}</TableCell>
                    <TableCell>{fmtEUR(o.amount)}</TableCell>
                    <TableCell><Badge className={o.payment_status === "paid" ? "bg-[#7AC143]" : "bg-[#FACC15] text-[#1A2E1A]"}>{o.payment_status}</Badge></TableCell>
                  </TableRow>
                ))}
                {orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#4B634B]">Aucune commande</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="bg-white border border-[#E5EAE5] rounded-2xl overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Nom</TableHead><TableHead>Rôle</TableHead><TableHead>Vérifié</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell><TableCell>{u.name}</TableCell>
                    <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                    <TableCell>{u.email_verified ? "✓" : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="home" className="mt-6">
          <HomePageCMS />
        </TabsContent>
      </Tabs>
    </div>
  );
}
